// src/utils/billingActions.js
//
// Écritures Firestore du suivi financier. Toutes passent par une transaction
// (lecture fraîche du document) pour ne jamais écraser une modification faite
// entre-temps par l'app ou par une Cloud Function.
//
// Champs écrits sur registrations/{id} — AUCUN champ géré par les Cloud
// Functions Mollie (installmentsPaid, installmentsTotal, nextPaymentDate,
// subscriptionStatus, installmentAmount) n'est touché :
//   • manualPayments[]   encaissements saisis à la main
//   • billingOverrides   réglages de l'échéancier (abonnements hors Mollie)
//   • paymentStatus      passé à 'paid' quand la 1re échéance est encaissée
//                        (et remis à 'pending' si on annule cet encaissement)
//   • consumedLessons / creditsUsed / creditsRemaining  (carte 10)
//
// Les Timestamp à l'intérieur des tableaux utilisent Timestamp.now() :
// serverTimestamp() est interdit dans un élément de tableau.

import { db } from '../firebase';
import { doc, runTransaction, Timestamp } from 'firebase/firestore';
import { cardCredits, consumptionMatches, isPaidStatus, round2 } from './billing';

const newId = () => `mp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

/** Enregistre un encaissement manuel sur une échéance */
export async function recordManualPayment(regId, { installmentNumber, amount, method, paidAt, note }) {
  const ref = doc(db, 'registrations', regId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('Inscription introuvable');
    const reg = snap.data();
    const list = Array.isArray(reg.manualPayments) ? reg.manualPayments : [];
    const n = Number(installmentNumber) || 1;
    const flipStatus = n === 1 && !isPaidStatus(reg.paymentStatus);
    const entry = {
      id: newId(),
      installmentNumber: n,
      amount: round2(amount),
      method: method || 'cash',
      paidAt: paidAt || null,          // 'YYYY-MM-DD'
      note: note || '',
      recordedAt: Timestamp.now(),
      setPaymentStatus: flipStatus,
    };
    const update = { manualPayments: [...list, entry], updatedAt: new Date() };
    if (flipStatus) {
      update.paymentStatus = 'paid';
      update.paymentUpdatedAt = new Date();
    }
    tx.set(ref, update, { merge: true });
  });
}

/** Annule un encaissement manuel */
export async function removeManualPayment(regId, entryId) {
  const ref = doc(db, 'registrations', regId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('Inscription introuvable');
    const reg = snap.data();
    const list = Array.isArray(reg.manualPayments) ? reg.manualPayments : [];
    const removed = list.find((m) => m.id === entryId);
    const next = list.filter((m) => m.id !== entryId);
    const update = { manualPayments: next, updatedAt: new Date() };
    // On ne remet "à payer" que si c'est NOUS qui avions passé le statut à payé
    if (removed?.setPaymentStatus && !next.some((m) => (Number(m.installmentNumber) || 1) === 1)) {
      update.paymentStatus = 'pending';
      update.paymentUpdatedAt = new Date();
    }
    tx.set(ref, update, { merge: true });
  });
}

/**
 * Réglages de l'échéancier pour un abonnement géré à la main.
 * Une valeur vide efface le réglage (null) → retour au calcul automatique.
 */
export async function saveBillingOverrides(regId, { startDate, installmentsTotal, firstInstallmentAmount, installmentAmount }) {
  const clean = (v, numeric) => {
    if (v === '' || v === undefined || v === null) return null;
    if (!numeric) return v;
    const n = Number(String(v).replace(',', '.'));
    return Number.isFinite(n) && n > 0 ? round2(n) : null;
  };
  const ref = doc(db, 'registrations', regId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('Inscription introuvable');
    tx.set(ref, {
      billingOverrides: {
        startDate:              clean(startDate, false),
        installmentsTotal:      clean(installmentsTotal, true),
        firstInstallmentAmount: clean(firstInstallmentAmount, true),
        installmentAmount:      clean(installmentAmount, true),
        updatedAt:              Timestamp.now(),
      },
      updatedAt: new Date(),
    }, { merge: true });
  });
}

/**
 * Décompte 1 crédit d'une carte 10 pour une séance.
 * @returns {Promise<'added'|'already'>}
 * @throws Error('CARD_EXHAUSTED') si la carte n'a plus de crédit
 */
export async function consumeCardCredit(regId, { dateKey, courseId, courseTitle, source }) {
  const ref = doc(db, 'registrations', regId);
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('Inscription introuvable');
    const reg = snap.data();
    const { consumed, used, total } = cardCredits(reg);
    if (consumed.some((cl) => consumptionMatches(cl, dateKey, courseId))) return 'already';
    if (used >= total) throw new Error('CARD_EXHAUSTED');
    const nextConsumed = [...consumed, {
      dateKey,
      courseId: courseId || null,
      courseTitle: courseTitle || null,
      source: source || 'admin',
      addedAt: Timestamp.now(),
    }];
    const creditsUsed = used + 1;
    tx.set(ref, {
      consumedLessons: nextConsumed,
      creditsUsed,
      creditsRemaining: Math.max(total - creditsUsed, 0),
      updatedAt: new Date(),
    }, { merge: true });
    return 'added';
  });
}

/**
 * Recrédite une séance sur une carte 10. Si onlySources est fourni, seules
 * les consommations créées par ces sources sont retirées (on ne touche pas
 * à ce que l'app a décompté lors d'une réservation).
 * @returns {Promise<boolean>} true si un crédit a été rendu
 */
export async function removeCardConsumption(regId, { dateKey, courseId, onlySources }) {
  const ref = doc(db, 'registrations', regId);
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) return false;
    const reg = snap.data();
    const { consumed, used, total } = cardCredits(reg);
    const idx = consumed.findIndex((cl) =>
      consumptionMatches(cl, dateKey, courseId) && (!onlySources || onlySources.includes(cl.source)));
    if (idx < 0) return false;
    const nextConsumed = consumed.filter((_, i) => i !== idx);
    const creditsUsed = Math.max(used - 1, 0);
    tx.set(ref, {
      consumedLessons: nextConsumed,
      creditsUsed,
      creditsRemaining: Math.max(total - creditsUsed, 0),
      updatedAt: new Date(),
    }, { merge: true });
    return true;
  });
}
